import type { WishlistProduct } from '../../graphql/codegen';
import type { ReactNode } from 'react';

import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';

import { WISHLIST_ENTRIES } from '../../constants/localStorageKeys';
import { fetchWishlistData } from '../../graphql/queries';
import { storageAvailable } from '../../helpers';
import { useAppContext } from '../static/AppContext';
import type { GlobalWishlist2 } from '~/amplienceTypes/schemas/imported/global-wishlist-schema';
import { useApolloContext } from '~/campaign/core/context/ApolloContext';
import { PageTypes } from '~/utilities/constants';

interface WishlistLocalStorageData {
  error: boolean;
  isFetched: boolean;
  products?: Array<WishlistProduct>;
}

// ============================================================================
// TODO: REMOVE AFTER APRIL 2026 (FS-2232 Migration)
// The following migration code can be safely removed after a few months in
// production when all guest users have had their localStorage migrated.
// To remove: Delete everything between these marker comments.
// ============================================================================

/**
 * Migration function for wishlist data stored in old format (before FS-2232).
 * Key changes:
 * - baseProductStockLevel → baseProductStockLevelStatus (field was incorrectly named)
 * - simplifiedImages: Array<{key, value}> → simplifiedImageData: { E01: value, ... }
 * - Removed mainColor (duplicate of color.description)
 * - Added __typename for GraphQL consistency
 *
 * This ensures guest users don't lose their wishlist data after the format change.
 *
 * @param oldData - Wishlist data in old format
 * @returns Transformed data in new format
 */
const migrateOldWishlistFormat = (oldData: WishlistLocalStorageData): WishlistLocalStorageData => {
  if (!oldData.products?.length) {
    return { isFetched: oldData.isFetched ?? true, products: [], error: false };
  }

  const migratedProducts = oldData.products.map(product => {
    const productAny = product as Record<string, unknown>;

    // If already in new format, return as-is
    if ('simplifiedImageData' in productAny || !('simplifiedImages' in productAny)) {
      return product;
    }

    // Convert simplifiedImages array to simplifiedImageData object
    const simplifiedImageData: Record<string, unknown> = {};
    const oldImages = productAny.simplifiedImages as Array<{ key: string; value: unknown }>;

    if (oldImages) {
      oldImages.forEach(img => {
        // Filter out __typename entries that were incorrectly stored in the array
        if (img.key && img.value && img.key !== '__typename') {
          simplifiedImageData[img.key] = img.value;
        }
      });
    }

    // Rename baseProductStockLevel to correct field name and remove old fields
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { simplifiedImages, baseProductStockLevel, mainColor, ...restProduct } = productAny;

    return {
      ...restProduct,
      baseProductStockLevelStatus: baseProductStockLevel, // Correct field name
      simplifiedImageData: Object.keys(simplifiedImageData).length > 0 ? simplifiedImageData : null,
      __typename: 'WishlistProduct',
    } as WishlistProduct;
  });

  return {
    isFetched: oldData.isFetched ?? true,
    products: migratedProducts,
    error: false,
  };
};

/**
 * Checks if the stored wishlist data is in the old format (before FS-2232).
 * Old format had simplifiedImages array instead of simplifiedImageData object.
 */
const needsMigration = (data: WishlistLocalStorageData): boolean => {
  if (!data?.products?.length) {
    return false;
  }

  const firstProduct = data.products[0] as Record<string, unknown>;

  return 'simplifiedImages' in firstProduct || 'baseProductStockLevel' in firstProduct;
};

// ============================================================================
// END MIGRATION CODE (FS-2232)
// ============================================================================

type WishlistContextType = {
  wishlist: WishlistLocalStorageData | undefined;
  content?: GlobalWishlist2;
  setWishlist: (wishlist: WishlistLocalStorageData) => void;
  fetchWishlist: (forceUpdate: boolean) => Promise<WishlistLocalStorageData | undefined>;
};

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const useWishlistContext = () => {
  const context = useContext(WishlistContext);

  if (context === undefined) {
    throw new Error('`useWishlistContext()` is used outside of a `<WishlistContext />`');
  }

  return context;
};

export const WishlistProvider = ({
  content,
  children,
}: {
  children: ReactNode;
  content?: GlobalWishlist2;
}) => {
  const [wishlist, setWishlist] = useState<WishlistLocalStorageData>();
  const { locale, siteType, pageType } = useAppContext();
  const { client: apolloClient } = useApolloContext();

  const fetchWishlist = useCallback(
    async (forceUpdate: boolean): Promise<WishlistLocalStorageData | undefined> => {
      const isLocalStorageAvailable = storageAvailable('localStorage');
      const localWishlistData = isLocalStorageAvailable
        ? localStorage.getItem(`${WISHLIST_ENTRIES}_${locale}`)
        : '';

      if (!forceUpdate && localWishlistData) {
        let parsedLocalWishlistData = JSON.parse(localWishlistData);

        // TODO: REMOVE MIGRATION CODE AFTER APRIL 2025 (FS-2232)
        // Check if data needs migration from old format (FS-2232)
        if (needsMigration(parsedLocalWishlistData)) {
          parsedLocalWishlistData = migrateOldWishlistFormat(parsedLocalWishlistData);

          // Save migrated data back to localStorage
          if (isLocalStorageAvailable) {
            localStorage.setItem(
              `${WISHLIST_ENTRIES}_${locale}`,
              JSON.stringify(parsedLocalWishlistData)
            );
          }
        }
        // END MIGRATION CODE (FS-2232)

        setWishlist(parsedLocalWishlistData);

        return parsedLocalWishlistData;
      }

      let parsedData: WishlistLocalStorageData = {
        error: true,
        isFetched: true,
        products: [],
      };

      try {
        const wishlistData = await fetchWishlistData({ siteType, locale, apolloClient });

        if (wishlistData?.wishlist?.results) {
          parsedData = {
            error: false,
            isFetched: true,
            products: wishlistData?.wishlist?.results || [],
          } as WishlistLocalStorageData;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      } finally {
        setWishlist(parsedData);

        if (isLocalStorageAvailable) {
          localStorage.setItem(`${WISHLIST_ENTRIES}_${locale}`, JSON.stringify(parsedData));
        }
      }

      return parsedData;
    },
    [locale, siteType, apolloClient]
  );

  useEffect(() => {
    const isWishlistPage = pageType === PageTypes.WISHLIST;

    fetchWishlist(isWishlistPage);
  }, [fetchWishlist, pageType]);

  const value = useMemo(
    () => ({
      wishlist,
      setWishlist,
      fetchWishlist,
      content,
    }),
    [fetchWishlist, wishlist, content]
  );

  return <WishlistContext value={value}>{children}</WishlistContext>;
};
